import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  VStack,
  HStack,
  Button,
  IconButton,
  Text,
  Input,
  InputGroup,
  InputLeftElement,
  SimpleGrid,
  Image,
  useToast,
  Spinner,
  Center
} from '@chakra-ui/react';
import {
  ChevronLeftIcon,
  SearchIcon,
  CloseIcon
} from '@chakra-ui/icons';
import api from '../../services/api';

const ProductSidebar = ({ isOpen, onClose, onProductSelect }) => {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const toast = useToast();

  // Debounced search effect
  useEffect(() => {
    if (!isOpen) return;
    
    const timeoutId = setTimeout(() => {
      loadProducts();
    }, 300); // 300ms debounce
    
    return () => clearTimeout(timeoutId);
  }, [isOpen, searchTerm]);

  const loadProducts = async () => {
    setIsLoading(true);
    try {
      const params = {};
      if (searchTerm.trim()) {
        params.search = searchTerm.trim();
      }
      
      const response = await api.get('/api/v1/products', { params });
      const productsData = response.data.data || response.data || [];
      setProducts(productsData);
      setFilteredProducts(productsData);
    } catch (error) {
      console.error('Error loading products:', error);
      toast({
        title: 'Error loading products',
        description: 'Failed to load products from server',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleProductClick = (product) => {
    onProductSelect(product);
    onClose();
    toast({
      title: 'Product selected',
      description: `${product.name} added to floor plan`,
      status: 'success',
      duration: 2000,
      isClosable: true,
    });
  };

  if (!isOpen) return null;

  return (
    <Box
      position="fixed"
      right={0}
      top="120px"
      bottom={0}
      width="400px"
      bg="white"
      borderLeft="1px"
      borderColor="gray.200"
      zIndex={30}
      overflow="hidden"
    >
      {/* Header */}
      <Box p={4} borderBottom="1px" borderColor="gray.200">
        <HStack justify="space-between" align="center">
          <Text fontWeight="bold" fontSize="lg">
            Add Product
          </Text>
          <IconButton
            icon={<CloseIcon />}
            variant="ghost"
            size="sm"
            onClick={onClose}
            aria-label="Close product sidebar"
          />
        </HStack>
      </Box>

      {/* Search Bar */}
      <Box p={4} borderBottom="1px" borderColor="gray.200">
        <InputGroup>
          <InputLeftElement pointerEvents="none">
            <SearchIcon color="gray.300" />
          </InputLeftElement>
          <Input
            placeholder="Search products..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="md"
          />
        </InputGroup>
      </Box>

      {/* Products Grid */}
      <Box flex={1} overflowY="auto" p={4}>
        {isLoading ? (
          <Center py={8}>
            <Spinner size="lg" />
          </Center>
        ) : filteredProducts.length === 0 ? (
          <Center py={8}>
            <Text color="gray.500">
              {searchTerm ? 'No products found' : 'No products available'}
            </Text>
          </Center>
        ) : (
          <SimpleGrid columns={2} spacing={4}>
            {filteredProducts.map((product) => (
              <Box
                key={product.id}
                border="1px"
                borderColor="gray.200"
                borderRadius="md"
                p={3}
                cursor="pointer"
                _hover={{
                  borderColor: "blue.300",
                  bg: "blue.50"
                }}
                onClick={() => handleProductClick(product)}
              >
                <VStack spacing={2} align="center">
                  {product.image_url ? (
                    <Image
                      src={product.image_url}
                      alt={product.name}
                      boxSize="60px"
                      objectFit="cover"
                      borderRadius="md"
                      fallbackSrc="https://via.placeholder.com/60x60?text=No+Image"
                    />
                  ) : (
                    <Box
                      boxSize="60px"
                      bg="gray.100"
                      borderRadius="md"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Text fontSize="xs" color="gray.500">No Image</Text>
                    </Box>
                  )}
                  <Text fontSize="sm" fontWeight="medium" textAlign="center" noOfLines={2}>
                    {product.name}
                  </Text>
                  {product.description && (
                    <Text fontSize="xs" color="gray.600" textAlign="center" noOfLines={2}>
                      {product.description}
                    </Text>
                  )}
                </VStack>
              </Box>
            ))}
          </SimpleGrid>
        )}
      </Box>
    </Box>
  );
};

export default ProductSidebar; 